/**
 * Security Context
 * ================
 * Banking-grade security implementation:
 * - Access token stored in memory (not localStorage)
 * - Refresh token in HttpOnly cookie (handled by browser)
 * - Idle timeout with session lock
 * - Sudo mode for sensitive operations
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const SecurityContext = createContext();

export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurity must be used within SecurityProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const SecurityProvider = ({ children }) => {
  // Access token stored in memory (not localStorage for security)
  const [accessToken, setAccessToken] = useState(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  
  // Security settings from backend
  const [securitySettings, setSecuritySettings] = useState({
    idle_timeout_minutes: 30,  // For admins
    vendor_idle_timeout_minutes: 240,  // For vendors (4 hours)
    access_token_expiry_minutes: 15,
    force_relogin_hours: 12,  // For admins
    vendor_force_relogin_hours: 168  // For vendors (7 days)
  });
  
  // Session lock state
  const [isSessionLocked, setIsSessionLocked] = useState(false);
  const [lockedUser, setLockedUser] = useState(null);
  
  // Sudo mode state
  const [sudoToken, setSudoToken] = useState(null);
  const [sudoExpiresAt, setSudoExpiresAt] = useState(null);
  const [showSudoModal, setShowSudoModal] = useState(false);
  const [pendingSudoCallback, setPendingSudoCallback] = useState(null);
  
  // Idle tracking
  const lastActivityRef = useRef(Date.now());
  const idleCheckIntervalRef = useRef(null);
  const tokenRefreshIntervalRef = useRef(null);

  // Fetch public security settings on mount
  useEffect(() => {
    const fetchSecuritySettings = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/security/settings/public`);
        if (response.ok) {
          const data = await response.json();
          // iter-307c — merge, don't replace. Callers (login/unlock) may
          // send a PARTIAL settings object (see below), so always keep
          // previously-known fields around instead of wiping them.
          setSecuritySettings((prev) => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error('Failed to fetch security settings:', error);
      }
    };
    
    fetchSecuritySettings();
  }, []);

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Cross-tab logout synchronization (sessionStorage doesn't fire storage events)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'auth_logout_broadcast') {
        // Another tab logged out — sync logout in this tab
        setAccessToken(null);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Lock session (don't clear refresh cookie) - defined early to be used in idle check
  const lockSession = useCallback(() => {
    const user = sessionStorage.getItem('user');
    if (user) {
      setLockedUser(JSON.parse(user));
    }
    setAccessToken(null);
    setIsSessionLocked(true);
    // Don't clear sessionStorage user - we need it for the lock screen
  }, []);

  // Set up activity listeners
  useEffect(() => {
    // Include 'click' for navigation and button interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];
    
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });
    
    // Also track route changes as activity (for SPA navigation)
    const handleRouteChange = () => {
      updateActivity();
    };
    window.addEventListener('popstate', handleRouteChange);
    
    // Track API calls as activity (dispatched from AuthContext)
    const handleApiActivity = () => {
      updateActivity();
    };
    window.addEventListener('api-activity', handleApiActivity);
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('api-activity', handleApiActivity);
    };
  }, [updateActivity]);

  // Idle check interval
  useEffect(() => {
    if (!accessToken) {
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
      }
      return;
    }

    const checkIdle = () => {
      const idleTime = Date.now() - lastActivityRef.current;
      
      // Get current user role to determine which timeout to use
      const storedUser = sessionStorage.getItem('user');
      const userRole = storedUser ? JSON.parse(storedUser)?.role : null;
      const isVendor = userRole === 'vendor';
      
      // Use vendor timeout (4 hours default) for vendors, admin timeout for admins
      const idleTimeoutMinutes = isVendor 
        ? securitySettings.vendor_idle_timeout_minutes 
        : securitySettings.idle_timeout_minutes;
      const idleTimeoutMs = idleTimeoutMinutes * 60 * 1000;
      
      // Debug logging for troubleshooting (only log every 5 mins to avoid spam)
      const idleMinutes = Math.floor(idleTime / 60000);
      const timeoutMinutes = idleTimeoutMinutes;
      
      // Only lock if truly idle AND we have an active access token
      if (idleTime >= idleTimeoutMs && !isSessionLocked && accessToken) {
        // Lock session due to inactivity
        lockSession();
      }
    };

    // Check every 30 seconds (less frequent to reduce overhead)
    idleCheckIntervalRef.current = setInterval(checkIdle, 30000);
    
    // Also run initial check
    checkIdle();
    
    return () => {
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
      }
    };
  }, [accessToken, securitySettings.idle_timeout_minutes, securitySettings.vendor_idle_timeout_minutes, isSessionLocked, lockSession]);

  // Token refresh state
  const refreshBackoffRef = useRef(0);       // consecutive failure count
  const refreshRetryTimeoutRef = useRef(null); // single active timer (interval OR backoff)
  const isRefreshingRef = useRef(false);      // mutex — only one call in-flight at a time

  // iter-230bd — Cross-tab refresh coordination. Without this, every
  // open tab independently scheduled its own refresh timer, causing the
  // "token refresh storm" Cloudflare flagged: 5 tabs → 5× /refresh-token
  // calls per minute, accumulating stale rows in `user_sessions` and
  // eventually tripping rate-limits at the 128th call (the 127×200
  // followed by 403 pattern). Each tab now generates a UUID, attempts
  // a short-TTL localStorage mutex before refreshing, and broadcasts
  // the new token to siblings via BroadcastChannel.
  const tabIdRef = useRef(null);
  const broadcastChannelRef = useRef(null);
  if (tabIdRef.current === null) {
    try {
      tabIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    } catch (_) {
      tabIdRef.current = `tab-${Date.now()}`;
    }
  }

  // BroadcastChannel for token sync across tabs. Mount once, close on unmount.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    let ch;
    try { ch = new BroadcastChannel('aniyor-auth-refresh'); } catch (_) { return undefined; }
    ch.onmessage = (event) => {
      const data = event?.data || {};
      // Ignore our own broadcasts (some browsers echo locally)
      if (data.originTabId === tabIdRef.current) return;
      if (data.type === 'token-refreshed' && data.access_token) {
        // Pick up the new token from the leader tab.
        setAccessToken(data.access_token);
        if (data.expires_in_seconds) {
          setTokenExpiresAt(Date.now() + (data.expires_in_seconds * 1000));
        }
        try { sessionStorage.setItem('token', data.access_token); } catch (_) { /* ignore */ }
        // Reset backoff — leader succeeded.
        refreshBackoffRef.current = 0;
      } else if (data.type === 'logout-broadcast') {
        // Another tab logged out — defer to existing storage-event handler.
      }
    };
    broadcastChannelRef.current = ch;
    return () => {
      try { ch.close(); } catch (_) { /* ignore */ }
      broadcastChannelRef.current = null;
    };
  }, []);

  // Token refresh with mutex + auto-backoff
  // KEY DESIGN: only ONE timer is ever active at a time (no interval + timeout racing each other)
  useEffect(() => {
    if (!accessToken || isSessionLocked) {
      clearTimeout(refreshRetryTimeoutRef.current);
      clearInterval(tokenRefreshIntervalRef.current);
      refreshRetryTimeoutRef.current = null;
      return;
    }

    // Normal interval between refreshes (1 min before token expires, min 60s)
    // iter-307c — defense-in-depth: `access_token_expiry_minutes` MUST be
    // finite here. If it's ever `undefined`/`NaN` (e.g. a future payload
    // shape change), `Math.max(NaN, 60000)` is STILL `NaN`, and
    // `setTimeout(fn, NaN)` fires IMMEDIATELY per spec — that was exactly
    // the root cause of a 300+ calls/sec refresh-token storm (see
    // `setTokensAfterLogin`'s merge fix). Never let a non-finite value
    // reach `setTimeout` again.
    const expiryMinutes = Number.isFinite(securitySettings.access_token_expiry_minutes)
      ? securitySettings.access_token_expiry_minutes
      : 15;
    const refreshInterval = Math.max(
      (expiryMinutes - 1) * 60 * 1000,
      60000
    );

    const scheduleNext = (delayMs) => {
      clearTimeout(refreshRetryTimeoutRef.current);
      clearInterval(tokenRefreshIntervalRef.current);
      refreshRetryTimeoutRef.current = setTimeout(doRefresh, Number.isFinite(delayMs) ? delayMs : 60000);
    };

    const doRefresh = async () => {
      // Mutex — skip if a call is already in flight
      if (isRefreshingRef.current) {
        scheduleNext(refreshInterval);
        return;
      }

      // iter-230bd — Cross-tab leader election via localStorage mutex.
      // Only ONE tab actually hits /refresh-token; followers wait for the
      // BroadcastChannel announcement and adopt the new token.
      const MUTEX_KEY = 'auth.refreshMutex';
      const MUTEX_TTL_MS = 10000;
      const acquireLeader = () => {
        try {
          const raw = localStorage.getItem(MUTEX_KEY);
          if (raw) {
            const m = JSON.parse(raw);
            if (m && m.expires > Date.now() && m.tabId !== tabIdRef.current) return false;
          }
          const slot = JSON.stringify({
            tabId: tabIdRef.current,
            acquired: Date.now(),
            expires: Date.now() + MUTEX_TTL_MS,
          });
          localStorage.setItem(MUTEX_KEY, slot);
          // Verify we won the race
          const verify = JSON.parse(localStorage.getItem(MUTEX_KEY) || '{}');
          return verify?.tabId === tabIdRef.current;
        } catch (_) {
          // localStorage unavailable (incognito quirks) — fall back to
          // single-tab behaviour to avoid blocking the user entirely.
          return true;
        }
      };
      const releaseLeader = () => {
        try {
          const raw = localStorage.getItem(MUTEX_KEY);
          if (raw && JSON.parse(raw).tabId === tabIdRef.current) {
            localStorage.removeItem(MUTEX_KEY);
          }
        } catch (_) { /* ignore */ }
      };

      if (!acquireLeader()) {
        // Another tab is refreshing. Schedule a short pickup-check; if
        // a fresh token landed in sessionStorage by then, adopt it.
        setTimeout(() => {
          try {
            const stored = sessionStorage.getItem('token');
            if (stored && stored !== accessToken) {
              setAccessToken(stored);
              // Conservative expiry: assume the access_token_expiry_minutes
              // setting; the next interval will resync the exact value.
              setTokenExpiresAt(
                Date.now() + (expiryMinutes * 60 * 1000)
              );
              refreshBackoffRef.current = 0;
            }
          } catch (_) { /* ignore */ }
        }, 2000);
        scheduleNext(refreshInterval);
        return;
      }

      isRefreshingRef.current = true;

      try {
        // Send refresh_token in body as fallback for cross-origin (third-party cookie blocking)
        const storedRefreshToken = sessionStorage.getItem('refresh_token');
        const response = await fetch(`${BACKEND_URL}/api/security/refresh-token`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: storedRefreshToken ? JSON.stringify({ refresh_token: storedRefreshToken }) : undefined
        });

        if (response.ok) {
          const data = await response.json();
          setAccessToken(data.access_token);
          setTokenExpiresAt(Date.now() + (data.expires_in_seconds * 1000));
          sessionStorage.setItem('token', data.access_token);
          lastActivityRef.current = Date.now();
          refreshBackoffRef.current = 0; // reset failures on success
          // iter-230bd — Broadcast the new token to sibling tabs so they
          // don't fire their own /refresh-token calls.
          try {
            broadcastChannelRef.current?.postMessage({
              type: 'token-refreshed',
              access_token: data.access_token,
              expires_in_seconds: data.expires_in_seconds,
              originTabId: tabIdRef.current,
            });
          } catch (_) { /* ignore */ }
          scheduleNext(refreshInterval);  // back to normal cadence
        } else if (response.status === 401) {
          refreshBackoffRef.current = 0;
          handleFullLogout();
          return; // no reschedule — logging out
        } else {
          // 4xx/5xx/503 — backoff, never logout
          refreshBackoffRef.current = Math.min(refreshBackoffRef.current + 1, 6);
          const backoffMs = Math.min(Math.pow(2, refreshBackoffRef.current) * 5000, 300000); // max 5 min
          console.warn(`[Security] Refresh failed (${response.status}). Retry in ${backoffMs / 1000}s`);
          scheduleNext(backoffMs);
        }
      } catch {
        // Network error / ERR_INSUFFICIENT_RESOURCES — backoff, never logout
        refreshBackoffRef.current = Math.min(refreshBackoffRef.current + 1, 6);
        const backoffMs = Math.min(Math.pow(2, refreshBackoffRef.current) * 5000, 300000); // max 5 min
        console.warn(`[Security] Refresh network error. Retry in ${backoffMs / 1000}s`);
        scheduleNext(backoffMs);
      } finally {
        isRefreshingRef.current = false;
        releaseLeader();
      }
    };

    // Kick off the first refresh after one normal interval
    scheduleNext(refreshInterval);

    return () => {
      clearTimeout(refreshRetryTimeoutRef.current);
      clearInterval(tokenRefreshIntervalRef.current);
    };
  }, [accessToken, securitySettings.access_token_expiry_minutes, isSessionLocked]);

  // Unlock session with password
  const unlockSession = useCallback(async (password) => {
    if (!lockedUser) {
      throw new Error('No locked user');
    }

      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: lockedUser.email,
          password: password
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAccessToken(data.access_token);
        setTokenExpiresAt(Date.now() + (data.expires_in_seconds * 1000));
        setIsSessionLocked(false);
        setLockedUser(null);
        lastActivityRef.current = Date.now();
        // Update sessionStorage so AuthContext picks up the new token
        sessionStorage.setItem('token', data.access_token);
        
        // Update security settings if provided
        if (data.security_settings) {
          // iter-307c — see setTokensAfterLogin below for the full
          // explanation of why this MUST merge, not replace.
          setSecuritySettings((prev) => ({ ...prev, ...data.security_settings }));
        }
        
        return { success: true };
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Invalid password');
      }
  }, [lockedUser]);

  // Deduplication lock — prevent concurrent/duplicate logout calls
  const isLoggingOutRef = useRef(false);

  // Full logout (clear everything)
  const handleFullLogout = useCallback(async () => {
    // Prevent multiple simultaneous logout calls (thundering herd guard)
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    try {
      await fetch(`${BACKEND_URL}/api/security/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
    } catch (error) {
      console.error('Logout API failed:', error);
    }

    // Determine redirect BEFORE clearing storage
    const storedUser = sessionStorage.getItem('user');
    const wasAdmin = storedUser && (JSON.parse(storedUser).role === 'admin' || JSON.parse(storedUser).role === 'super_admin');
    
    setAccessToken(null);
    setTokenExpiresAt(null);
    setIsSessionLocked(false);
    setLockedUser(null);
    setSudoToken(null);
    setSudoExpiresAt(null);
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refresh_token');
    
    // Redirect to login
    window.location.href = wasAdmin ? '/admin/login' : '/login';
  }, [accessToken]);

  // Set tokens after successful login
  const setTokensAfterLogin = useCallback((newAccessToken, expiresInSeconds, settings) => {
    setAccessToken(newAccessToken);
    setTokenExpiresAt(Date.now() + (expiresInSeconds * 1000));
    lastActivityRef.current = Date.now();

    if (settings) {
      // iter-307c — ROOT CAUSE of the 401/token-refresh storm (300+
      // calls/sec to /api/security/refresh-token): `/api/auth/login`'s
      // `security_settings` payload only ever contained
      // `{idle_timeout_minutes, force_relogin_hours}` — NOT
      // `access_token_expiry_minutes`. A plain `setSecuritySettings(settings)`
      // REPLACED the whole state object, wiping the `access_token_expiry_minutes:
      // 15` default down to `undefined`. The proactive-refresh effect then
      // computed `refreshInterval = Math.max((undefined - 1) * 60000, 60000)`
      // = `NaN`, and `setTimeout(doRefresh, NaN)` fires IMMEDIATELY (browsers
      // treat a NaN delay as 0) — every single time, forever, since
      // `doRefresh` re-schedules itself with the same NaN interval on every
      // completion. Merging keeps whatever fields the login response didn't
      // send intact.
      setSecuritySettings((prev) => ({ ...prev, ...settings }));
    }

    // For backward compatibility, also store in sessionStorage
    // This will be removed once all components use the security context
    sessionStorage.setItem('token', newAccessToken);
  }, []);

  // Verify sudo mode
  const verifySudo = useCallback(async (password) => {
      const response = await fetch(`${BACKEND_URL}/api/security/verify-sudo`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken || sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        const data = await response.json();
        setSudoToken(data.sudo_token);
        setSudoExpiresAt(Date.now() + (data.expires_in_seconds * 1000));
        return { success: true, sudoToken: data.sudo_token };
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Invalid password');
      }
  }, [accessToken]);

  // Check if sudo is active
  const isSudoActive = useCallback(() => {
    if (!sudoToken || !sudoExpiresAt) return false;
    return Date.now() < sudoExpiresAt;
  }, [sudoToken, sudoExpiresAt]);

  // Get sudo token (or prompt for password)
  const getSudoToken = useCallback(() => {
    if (isSudoActive()) {
      return Promise.resolve(sudoToken);
    }
    
    // Need to prompt for password
    return new Promise((resolve, reject) => {
      setPendingSudoCallback({ resolve, reject });
      setShowSudoModal(true);
    });
  }, [isSudoActive, sudoToken]);

  // Handle sudo modal completion
  const handleSudoComplete = useCallback((token) => {
    setShowSudoModal(false);
    if (pendingSudoCallback) {
      pendingSudoCallback.resolve(token);
      setPendingSudoCallback(null);
    }
  }, [pendingSudoCallback]);

  // Handle sudo modal cancel
  const handleSudoCancel = useCallback(() => {
    setShowSudoModal(false);
    if (pendingSudoCallback) {
      pendingSudoCallback.reject(new Error('User cancelled sudo verification'));
      setPendingSudoCallback(null);
    }
  }, [pendingSudoCallback]);

  // Make authenticated API call with automatic sudo handling
  const secureApiCall = useCallback(async (endpoint, options = {}) => {
    const url = `${BACKEND_URL}${endpoint}`;
    const token = accessToken || sessionStorage.getItem('token');
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add sudo token if available and active
    if (options.requireSudo) {
      const sudoTok = await getSudoToken();
      headers['X-Sudo-Token'] = sudoTok;
    } else if (isSudoActive()) {
      headers['X-Sudo-Token'] = sudoToken;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });

    // Handle sudo required error
    if (response.status === 403) {
      const error = await response.json();
      if (error.detail?.code === 'SUDO_REQUIRED' || error.detail?.code === 'SUDO_EXPIRED') {
        // Prompt for sudo and retry
        const sudoTok = await getSudoToken();
        headers['X-Sudo-Token'] = sudoTok;
          
          // Retry the request
          const retryResponse = await fetch(url, {
            ...options,
            headers,
            credentials: 'include'
          });
          
          return retryResponse;
      }
    }

    return response;
  }, [accessToken, getSudoToken, isSudoActive, sudoToken]);

  const value = {
    // Token management
    accessToken,
    setTokensAfterLogin,
    
    // Session lock
    isSessionLocked,
    lockedUser,
    lockSession,
    unlockSession,
    
    // Logout
    handleFullLogout,
    
    // Sudo mode
    verifySudo,
    isSudoActive,
    getSudoToken,
    sudoToken,
    showSudoModal,
    handleSudoComplete,
    handleSudoCancel,
    
    // Security settings
    securitySettings,
    
    // Secure API calls
    secureApiCall,
    
    // Activity tracking
    updateActivity
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};

export default SecurityContext;
